import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Button,
  Table,
  Typography,
  Space,
  Tag,
  message,
  Spin,
  Row,
  Col,
  Alert,
  Checkbox,
  Statistic,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  PlusOutlined,
  CloudDownloadOutlined,
} from '@ant-design/icons';
import { pendingInvoicesApi } from '../services/api';

const { Title, Text } = Typography;

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  accountCode: string;
}

interface ReconciliationData {
  pendingInvoiceId: string;
  xeroInvoiceId: string;
  xeroStatus: string;
  canPushToXero: boolean;
  xeroTotal: number;
  atlasTotal: number;
  discrepancy: number;
  comparison: {
    matched: Array<{ atlasItem: LineItem; xeroItem: LineItem; matchScore: number }>;
    atlasOnly: LineItem[];
    xeroOnly: LineItem[];
  };
}

const InvoiceReconciler: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedXeroItems, setSelectedXeroItems] = useState<string[]>([]);

  const { data, isLoading, error } = useQuery<ReconciliationData>({
    queryKey: ['invoice-reconciliation', id],
    queryFn: async () => {
      const response = await pendingInvoicesApi.getReconciliation(id!);
      return response as unknown as ReconciliationData;
    },
    enabled: !!id,
  });

  const saveToAtlasMutation = useMutation({
    mutationFn: async () => {
      const response = await pendingInvoicesApi.saveXeroItemsToAtlas(id!, selectedXeroItems);
      return response as unknown as { itemsAdded: number };
    },
    onSuccess: (result) => {
      message.success(`${result.itemsAdded} item(s) imported from Xero`);
      setSelectedXeroItems([]);
      queryClient.invalidateQueries({ queryKey: ['invoice-reconciliation', id] });
    },
    onError: () => {
      message.error('Failed to import items from Xero');
    },
  });

  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Failed to load reconciliation data" />
      </div>
    );
  }

  const { comparison, xeroTotal, atlasTotal, discrepancy, xeroStatus, canPushToXero, xeroInvoiceId } = data;
  const hasDiscrepancy = Math.abs(discrepancy) > 0.01;

  const lineItemColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'right' as const,
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right' as const,
      render: (val: number) => formatCurrency(val),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right' as const,
      render: (val: number) => formatCurrency(val),
    },
  ];

  const xeroOnlyColumns = [
    {
      title: '',
      key: 'select',
      width: 50,
      render: (_: any, record: any) => (
        <Checkbox
          checked={selectedXeroItems.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedXeroItems([...selectedXeroItems, record.id]);
            } else {
              setSelectedXeroItems(selectedXeroItems.filter(id => id !== record.id));
            }
          }}
        />
      ),
    },
    ...lineItemColumns,
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        Back
      </Button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>Invoice Reconciliation</Title>
          <Space>
            <Tag color="blue">Xero Status: {xeroStatus}</Tag>
            {hasDiscrepancy ? (
              <Tag color="orange" icon={<WarningOutlined />}>Discrepancy: {formatCurrency(discrepancy)}</Tag>
            ) : (
              <Tag color="green" icon={<CheckCircleOutlined />}>Reconciled</Tag>
            )}
          </Space>
        </div>
        <Button 
          type="link" 
          href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${xeroInvoiceId}`}
          target="_blank"
        >
          View in Xero →
        </Button>
      </div>

      <Row gutter={24} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic title="Atlas Total" value={atlasTotal} precision={2} prefix="$" />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Xero Total" value={xeroTotal} precision={2} prefix="$" />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic 
              title="Difference" 
              value={Math.abs(discrepancy)} 
              precision={2} 
              prefix={discrepancy >= 0 ? '+$' : '-$'}
              valueStyle={{ color: hasDiscrepancy ? '#faad14' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {comparison.matched.length > 0 && (
        <Card 
          title={<><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />Matched Items ({comparison.matched.length})</>}
          style={{ marginBottom: 24 }}
        >
          <Table
            dataSource={comparison.matched.map((m: { atlasItem: LineItem; matchScore: number }, i: number) => ({ ...m.atlasItem, key: i, matchScore: m.matchScore }))}
            columns={[
              ...lineItemColumns,
              {
                title: 'Match',
                key: 'match',
                width: 80,
                render: (_: any, record: any) => (
                  <Tag color="green">{Math.round(record.matchScore * 100)}%</Tag>
                ),
              },
            ]}
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {comparison.xeroOnly.length > 0 && (
        <Card 
          title={
            <Space>
              <PlusOutlined style={{ color: '#1890ff' }} />
              <span>In Xero Only ({comparison.xeroOnly.length})</span>
              <Text type="secondary">- Items added in Xero but not in Atlas</Text>
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              disabled={selectedXeroItems.length === 0}
              loading={saveToAtlasMutation.isPending}
              onClick={() => saveToAtlasMutation.mutate()}
            >
              Import {selectedXeroItems.length > 0 ? `(${selectedXeroItems.length})` : ''} to Atlas
            </Button>
          }
          style={{ marginBottom: 24 }}
        >
          <Alert
            message="These items exist in Xero but not in Atlas"
            description="Select items and click 'Import to Atlas' to sync them."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            dataSource={comparison.xeroOnly.map((item: LineItem, i: number) => ({ ...item, key: i }))}
            columns={xeroOnlyColumns}
            pagination={false}
            size="small"
            rowSelection={{
              selectedRowKeys: selectedXeroItems,
              onChange: (keys) => setSelectedXeroItems(keys as string[]),
            }}
          />
        </Card>
      )}

      {comparison.atlasOnly.length > 0 && (
        <Card 
          title={
            <Space>
              <WarningOutlined style={{ color: '#faad14' }} />
              <span>In Atlas Only ({comparison.atlasOnly.length})</span>
              <Text type="secondary">- Items in Atlas but missing from Xero</Text>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Alert
            message={canPushToXero 
              ? "These items exist in Atlas but not in Xero. You can push them to Xero since the invoice is still in DRAFT status."
              : `Cannot push to Xero - invoice is ${xeroStatus}. Only DRAFT invoices can be modified.`
            }
            type={canPushToXero ? "warning" : "error"}
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            dataSource={comparison.atlasOnly.map((item: LineItem, i: number) => ({ ...item, key: i }))}
            columns={lineItemColumns}
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {!hasDiscrepancy && comparison.xeroOnly.length === 0 && comparison.atlasOnly.length === 0 && (
        <Alert
          message="Invoice is fully reconciled"
          description="All line items match between Atlas and Xero."
          type="success"
          showIcon
        />
      )}
    </div>
  );
};

export default InvoiceReconciler;
